import { Flex, Form, FormInstance, InputNumber, Select, Switch } from "antd";
import { DiffRow, fmtBool } from "pages/settings/policy/PolicySettingsPage";
import { PolicySection } from "pages/settings/policy/policySection";
import { SettingRow, SettingsSection } from "pages/settings/settingsRows";
import { translate } from "shared/languages";
import { ExpiryUnit, ValidityStartScope } from "shared/types/enum";

const KEY = "validity-start";

type Values = {
  validity_start_first_use: boolean;
  validity_start_scope: ValidityStartScope;
  auto_start_value: number;
  auto_start_unit: ExpiryUnit;
};

// Dropdown order: every product, memberships only, packages only.
const SCOPE_ORDER = [ValidityStartScope.All, ValidityStartScope.Membership, ValidityStartScope.Package];

// The grace period is day/month only (the server refuses "year"): 1-730 days
// or 1-24 months, mirroring the parser.
const DEADLINE_MAX: Record<ExpiryUnit.Day | ExpiryUnit.Month, number> = {
  [ExpiryUnit.Day]: 730,
  [ExpiryUnit.Month]: 24,
};

const scopeLabel = (scope: ValidityStartScope) =>
  translate(`class_settings.validity_start.scope_${scope}`);
const unitLabel = (unit: ExpiryUnit) =>
  translate(unit === ExpiryUnit.Day ? "class_settings.unit.days" : "class_settings.unit.months");
const deadlineLabel = (value: number, unit: ExpiryUnit) => `${value} ${unitLabel(unit)}`;

// Masa Berlaku Produk: whether a product sold from now on runs its validity
// from the purchase date (the default) or from the client's first booked
// class, plus the grace period after which a never-used package's clock
// starts on its own (required while the rule is on). Scope and grace keep
// their stored value while off and are neither listed nor sent then. Governs
// future sales only; sold packages snapshot the rule they were sold under
// (server util/package-activation.ts).
const Fields = ({ form }: { form: FormInstance }) => {
  const enabled = Form.useWatch("validity_start_first_use", form);
  const unit = Form.useWatch("auto_start_unit", form) ?? ExpiryUnit.Day;
  const toggleLabel = translate("class_settings.validity_start.toggle");
  const scopeFieldLabel = translate("class_settings.validity_start.scope");
  const deadlineFieldLabel = translate("class_settings.validity_start.auto_start");
  // Built at render time: translate() is not reactive at module scope.
  const scopeOptions = SCOPE_ORDER.map((value) => ({ value, label: scopeLabel(value) }));
  const unitOptions = [ExpiryUnit.Day, ExpiryUnit.Month].map((value) => ({
    value,
    label: unitLabel(value),
  }));
  const deadlineMax = DEADLINE_MAX[unit === ExpiryUnit.Day ? ExpiryUnit.Day : ExpiryUnit.Month];

  return (
    <SettingsSection
      id={KEY}
      title={translate("class_settings.validity_start.title")}
      description={translate("settings_nav.desc.validity_start")}
    >
      <SettingRow
        last={!enabled}
        title={toggleLabel}
        hint={translate("class_settings.validity_start.toggle_desc")}
        control={
          <Form.Item name="validity_start_first_use" valuePropName="checked" noStyle>
            <Switch aria-label={toggleLabel} />
          </Form.Item>
        }
      />
      {/* Scope and the grace period unmount while off; AntD keeps their
          values in the form store. */}
      {enabled && (
        <>
          <SettingRow
            title={scopeFieldLabel}
            control={
              <Form.Item name="validity_start_scope" noStyle>
                <Select aria-label={scopeFieldLabel} options={scopeOptions} style={{ width: 200 }} />
              </Form.Item>
            }
          />
          <SettingRow
            last
            title={deadlineFieldLabel}
            hint={translate("class_settings.validity_start.auto_start_desc")}
            control={
              // Two plain side-by-side controls, never Space.Compact (see the
              // product form's Masa Berlaku). The wrapper Form.Item shows the
              // value's validation message.
              <Form.Item style={{ margin: 0 }}>
                <Flex gap={8} align="center" justify="flex-end">
                  <Form.Item
                    name="auto_start_value"
                    noStyle
                    rules={[
                      {
                        required: true,
                        type: "integer",
                        min: 1,
                        max: deadlineMax,
                        message: translate("class_settings.validity_start.auto_start_required"),
                      },
                    ]}
                  >
                    <InputNumber
                      aria-label={deadlineFieldLabel}
                      min={1}
                      max={deadlineMax}
                      precision={0}
                      style={{ width: 90 }}
                    />
                  </Form.Item>
                  <Form.Item name="auto_start_unit" noStyle>
                    <Select
                      aria-label={translate("label.unit")}
                      options={unitOptions}
                      style={{ width: 102 }}
                    />
                  </Form.Item>
                </Flex>
              </Form.Item>
            }
          />
        </>
      )}
    </SettingsSection>
  );
};

export const validityStartPolicy: PolicySection = {
  key: KEY,
  initialValues: (s) => ({
    validity_start_first_use: s.validity_start_first_use,
    validity_start_scope: s.validity_start_scope,
    auto_start_value: s.auto_start_value,
    auto_start_unit: s.auto_start_unit,
  }),
  buildSave: (v: Values, s) => {
    const isEnabled = !!v.validity_start_first_use;
    const nextScope = v.validity_start_scope ?? s.validity_start_scope;
    const nextValue = v.auto_start_value ?? s.auto_start_value;
    const nextUnit = v.auto_start_unit ?? s.auto_start_unit;
    const rows: DiffRow[] = [];
    if (isEnabled !== s.validity_start_first_use) {
      rows.push({
        label: translate("class_settings.validity_start.toggle"),
        before: fmtBool(s.validity_start_first_use),
        after: fmtBool(isEnabled),
      });
    }
    // Scope and deadline only matter while the rule is on: a change made
    // while off is neither listed nor sent, and the stored value is kept.
    if (isEnabled && nextScope !== s.validity_start_scope) {
      rows.push({
        label: translate("class_settings.validity_start.scope"),
        before: scopeLabel(s.validity_start_scope),
        after: scopeLabel(nextScope),
      });
    }
    if (isEnabled && (nextValue !== s.auto_start_value || nextUnit !== s.auto_start_unit)) {
      rows.push({
        label: translate("class_settings.validity_start.auto_start"),
        before: deadlineLabel(s.auto_start_value, s.auto_start_unit),
        after: deadlineLabel(nextValue, nextUnit),
      });
    }
    const note = translate(
      !isEnabled
        ? "class_settings.confirm.note_validity_start_off"
        : nextScope === ValidityStartScope.Membership
        ? "class_settings.confirm.note_validity_start_on_membership"
        : nextScope === ValidityStartScope.Package
        ? "class_settings.confirm.note_validity_start_on_package"
        : "class_settings.confirm.note_validity_start_on",
    );
    return {
      rows,
      payload: {
        validity_start_first_use: isEnabled,
        ...(isEnabled
          ? {
              validity_start_scope: nextScope,
              auto_start_value: nextValue,
              auto_start_unit: nextUnit,
            }
          : {}),
      },
      note,
    };
  },
  Fields,
};
